import { useId, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CurecallInputLabel, CurecallSelect, CurecallSelectItem } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import {
  DurationInputFields,
  type TDelayState,
} from 'src/components/duration-input-fields/duration-input-fields.component'
import { formatCombinedIsoDuration, parseCombinedIsoDuration } from 'src/utils/duration-iso.util'
import { EHumanReviewRequestSubject } from 'src/pages/dashboard/_components/human-review-requests.type'
import { twMerge } from 'src/utils/tw-merge.util'
import { TProgrammedMessageFormValues } from './programmed-message.type'

const DEFAULT_SMS_DELAY: TDelayState = { days: 0, hours: 1, minutes: 0 }

export default function DashboardSection({ animationKey = 0 }: { animationKey?: number }) {
  const { t } = useTranslation()
  const delayInputId = useId()

  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<TProgrammedMessageFormValues>()

  const subject = useWatch({ control, name: 'humanReviewCheck.subject' })
  const unreceivedSmsDelay = useWatch({ control, name: 'humanReviewCheck.unreceivedSmsDelay' })

  const [delay, setDelay] = useState<TDelayState>(() => {
    if (unreceivedSmsDelay) {
      const parsed = parseCombinedIsoDuration(unreceivedSmsDelay)
      return { days: parsed.days, hours: parsed.hours, minutes: parsed.minutes }
    }
    return DEFAULT_SMS_DELAY
  })

  const tabOptions: CurecallSelectItem<EHumanReviewRequestSubject | null>[] = useMemo(
    () => [
      {
        label: t('pages.dashboard.human-review-requests.tabs.convocation'),
        value: EHumanReviewRequestSubject.CONVOCATION,
      },
      {
        label: t('pages.dashboard.human-review-requests.tabs.post-op-control'),
        value: EHumanReviewRequestSubject.POST_OP_CONTROL,
      },
      {
        label: t('pages.dashboard.human-review-requests.tabs.administrative-documents'),
        value: EHumanReviewRequestSubject.ADMINISTRATIVE_DOCUMENTS,
      },
      {
        label: t('pages.dashboard.human-review-requests.tabs.medical-documents'),
        value: EHumanReviewRequestSubject.MEDICAL_DOCUMENTS,
      },
    ],
    [t],
  )

  const updateDelay = (newDelay: TDelayState) => {
    setDelay(newDelay)
    setValue('humanReviewCheck.unreceivedSmsDelay', formatCombinedIsoDuration(newDelay))
    clearErrors('humanReviewCheck.unreceivedSmsDelay')
  }

  const handleClear = () => {
    setValue('humanReviewCheck.subject', null)
    setValue('humanReviewCheck.unreceivedSmsDelay', null)
    clearErrors('humanReviewCheck.subject')
    clearErrors('humanReviewCheck.unreceivedSmsDelay')
    setDelay(DEFAULT_SMS_DELAY)
  }

  return (
    <div className="c-flex c-flex-col c-gap-4">
      <div className="c-flex c-items-end c-gap-3">
        <Controller
          control={control}
          name="humanReviewCheck.subject"
          render={({ field }) => (
            <div
              key={animationKey}
              className={twMerge(
                'c-flex c-flex-col c-gap-1 c-w-[500px]',
                animationKey > 0 && 'c-animate-input-flash',
              )}
            >
              <CurecallSelect
                label={t('pages.configuration.messages.dashboard-tab-label')}
                labelClassName="c-whitespace-nowrap"
                items={tabOptions}
                onChange={(option) => {
                  setValue('humanReviewCheck.subject', option.value, { shouldDirty: true })
                  clearErrors('humanReviewCheck.subject')
                  if (!unreceivedSmsDelay) {
                    setValue(
                      'humanReviewCheck.unreceivedSmsDelay',
                      formatCombinedIsoDuration(DEFAULT_SMS_DELAY),
                    )
                  }
                }}
                selectedItem={tabOptions.find((option) => option.value === field.value)}
                placeholder={t('pages.configuration.messages.dashboard-tab-placeholder')}
              />
              {errors.humanReviewCheck?.subject && (
                <span className="c-text-sm c-text-danger-600">
                  {t('pages.configuration.messages.dashboard-tab-required-error')}
                </span>
              )}
            </div>
          )}
        />

        {subject && (
          <button
            type="button"
            onClick={handleClear}
            className="c-pb-2 c-text-sm c-text-primary-600 hover:c-text-primary-800 hover:c-underline c-whitespace-nowrap"
          >
            {t('common.actions.delete')}
          </button>
        )}
      </div>

      {subject && (
        <div className="c-flex c-flex-col c-gap-2 c-pl-6 c-border-l-2 c-border-gray-200">
          <CurecallInputLabel
            id={delayInputId}
            label={t('pages.configuration.messages.sms-not-received-delay')}
            isRequired={true}
            tooltipContent={t('pages.configuration.messages.sms-not-received-delay-tooltip')}
            tooltipPosition="right"
          />
          <Controller
            control={control}
            name="humanReviewCheck.unreceivedSmsDelay"
            rules={{
              validate: (value) => {
                if (!subject) return true
                if (!value) return false
                const { days, hours, minutes } = parseCombinedIsoDuration(value)
                return days * 24 * 60 + hours * 60 + minutes >= 5
              },
            }}
            render={() => (
              <div className="c-flex c-flex-col c-gap-2">
                <DurationInputFields delay={delay} onDelayChange={updateDelay} />
                {errors.humanReviewCheck?.unreceivedSmsDelay && (
                  <span className="c-text-sm c-text-danger-600">
                    {t('pages.configuration.messages.sms-not-received-delay-error')}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
}
